import { t } from "@lingui/macro";
import {
  NavigationProp,
  useIsFocused,
  useNavigation,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { AuthenticationModal } from "../../../features/Authentication/components/AuthenticationModal";
import {
  ActiveClubLanguageProvider,
  LocaleTransitionBoundary,
} from "../../../features/LanguagePreference";
import { UnauthenticatedNavigatorParamList } from "../../../navigators/UnauthenticatedStackNavigator/types";
import { RegisterAccountContainerQuery } from "./__generated__/RegisterAccountContainerQuery.graphql";

export const RegisterAccountContainer = ({
  queryRef,
  token,
}: {
  queryRef: PreloadedQuery<RegisterAccountContainerQuery>;
  token: string;
}) => {
  const data = usePreloadedQuery(RegisterAccountContainerQueryString, queryRef);
  const isFocused = useIsFocused();
  const email = data.setPasswordToken?.email;

  const navigation =
    useNavigation<NavigationProp<UnauthenticatedNavigatorParamList>>();

  const { showToast } = useToast();
  useEffect(() => {
    if (!data.setPasswordToken) {
      showToast({
        description: t`Whoops! Looks like that registration link isn't valid. Please check your email again, or contact the club for assistance.`,
        semanticStyle: "error",
        toastId: "registration-token-error",
      });

      navigation.navigate("Initial");
    }
  }, [data.setPasswordToken, navigation, showToast]);

  return (
    <ActiveClubLanguageProvider
      active={isFocused}
      locale={data.setPasswordToken?.club.defaultLocale}
    >
      <LocaleTransitionBoundary>
        <AuthenticationModal
          isOpen={true}
          onComplete={() => {}}
          clubSlug={data.setPasswordToken?.club.slug}
          hideCloseButton
          initialStep={{
            step: "SetPassword",
            backStep: "Initial",
            verifiedEmail: email ?? "",
            token,
          }}
        />
      </LocaleTransitionBoundary>
    </ActiveClubLanguageProvider>
  );
};

export const RegisterAccountContainerQueryString = graphql`
  query RegisterAccountContainerQuery($token: String!, $userContext: String) {
    # TODO(ENG-2770): Require $userContext after all legacy reset clients and links are retired.
    setPasswordToken(token: $token, userContext: $userContext) {
      email
      club {
        defaultLocale
        name
        slug
        logoImage {
          format
          height
          # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
          publicId: renderingKey
          width
        }
      }
    }
  }
`;
